import React from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import { ArrowLeft, FileText, Shield, Scale, Cookie, Lock, Loader2 } from 'lucide-react';

// Page configuration mapping
const PAGE_CONFIG = {
  legal_mentions: {
    title: 'Mentions légales',
    icon: FileText,
    htmlKey: 'legal_mentions_html',
    description: 'Informations légales concernant le site et l\'entreprise'
  },
  cgv: {
    title: 'Conditions Générales de Vente',
    icon: Scale,
    htmlKey: 'cgv_html',
    description: 'Conditions applicables à nos prestations'
  },
  cgu: {
    title: 'Conditions Générales d\'Utilisation',
    icon: Shield,
    htmlKey: 'cgu_html',
    description: 'Règles d\'utilisation du site'
  },
  privacy_policy: {
    title: 'Politique de confidentialité',
    icon: Lock,
    htmlKey: 'privacy_policy_html',
    description: 'Comment nous protégeons vos données personnelles'
  },
  cookies_policy: {
    title: 'Politique des cookies',
    icon: Cookie,
    htmlKey: 'cookies_policy_html',
    description: 'Utilisation des cookies sur notre site'
  }
};

export default function LegalPage({ pageType }) {
  const { config, loading } = useConfig();
  const pageConfig = PAGE_CONFIG[pageType] || PAGE_CONFIG.legal_mentions;
  const Icon = pageConfig.icon;

  // Get the HTML content from the configuration
  const htmlContent = config?.legal_texts?.[pageConfig.htmlKey] || '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid={`legal-page-${pageType}`}>
      {/* Header */}
      <header className="header-glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={config?.logo_url || 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png'} 
              alt={config?.company_name || 'Mitonner'} 
              className="h-10" 
            />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à l&apos;accueil</span>
          </Link>
        </div>
      </header>

      {/* Page Title */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-[#A5BBA8]/10 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#A5BBA8]/20 text-[#5C6A61] rounded-full mb-6">
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">Document légal</span>
          </div>
          
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl font-medium text-[#2A312C] mb-4">
            {pageConfig.title}
          </h1>
          
          <p className="text-lg text-[#5C6A61] max-w-2xl mx-auto">
            {pageConfig.description}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-default">
            {htmlContent ? (
              <div 
                className="legal-content prose prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="text-center py-12">
                <Icon className="w-16 h-16 text-[#E3DED5] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#2A312C] mb-2">
                  Contenu en cours de rédaction
                </h3>
                <p className="text-[#5C6A61]">
                  Cette page sera bientôt disponible. Veuillez nous contacter pour toute question.
                </p>
                <div className="mt-6">
                  <a 
                    href={`mailto:${config?.email || 'contact@mitonner-essentiel.fr'}`}
                    className="btn-outline inline-flex items-center gap-2"
                  >
                    Nous contacter
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Company Info Footer */}
      <section className="py-8 border-t border-[#E3DED5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-[#5C6A61]">
            <p className="font-medium text-[#2A312C] mb-2">{config?.company_name || 'Mitonner'}</p>
            {config?.company_legal_name && (
              <p className="mb-1">{config.company_legal_name}</p>
            )}
            {config?.siret && (
              <p className="mb-1">SIRET : {config.siret}</p>
            )}
            {config?.sap_number && config?.sap_enabled && (
              <p className="mb-1">Service à la Personne : {config.sap_number}</p>
            )}
            {config?.address && (
              <p className="mb-1">{config.address} {config.postal_code} {config.city}</p>
            )}
            {config?.email && (
              <p>Email : <a href={`mailto:${config.email}`} className="text-[#A5BBA8] hover:underline">{config.email}</a></p>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#2A312C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <img 
              src={config?.logo_url || 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png'} 
              alt={config?.company_name || 'Mitonner'} 
              className="h-8 mx-auto brightness-0 invert" 
            />
          </Link>
          <p className="text-sm text-[#A5BBA8]">
            {config?.footer_text || `© ${new Date().getFullYear()} Mitonner. Tous droits réservés.`}
          </p>
        </div>
      </footer>
    </div>
  );
}
